import type { Draft, Question } from './types';

// Strip the legacy "-N" suffix that disambiguates duplicate fill-in options.
const stripSuffix = (s: string) => s.replace(/-\d+$/, '');

// Whether the user has filled in enough to allow "Comprobar".
export function isComplete(q: Question, d: Draft): boolean {
  switch (q.format) {
    case 'options':
      return d.format === 'options' && d.value !== null;
    case 'true_false':
      return d.format === 'true_false' && d.value !== null;
    case 'element_order':
      // Always full — the draft holds a complete permutation, so the user
      // can submit whenever they like.
      return d.format === 'element_order';
    case 'fill_blank':
      return d.format === 'fill_blank' && d.blanks.every((b) => b !== null);
    case 'grouping':
      return (
        d.format === 'grouping' && d.assignments.every((a) => a !== null)
      );
    case 'matching':
      return d.format === 'matching' && d.matches.every((m) => m !== null);
    case 'open_ended':
      return d.format === 'open_ended' && d.text.trim().length > 0;
  }
}

// Synchronous correctness. Throws for open_ended (uses the IA evaluator).
export function isCorrect(q: Question, d: Draft): boolean {
  switch (q.format) {
    case 'options':
      return d.format === 'options' && d.value === q.correct_answer;

    case 'true_false': {
      if (d.format !== 'true_false') return false;
      const expected =
        typeof q.correct_answer === 'boolean'
          ? q.correct_answer
          : Boolean(q.correct_answer);
      return d.value === expected;
    }

    case 'element_order': {
      if (d.format !== 'element_order') return false;
      const expected = q.correct_order;
      return d.order.every((s, i) => s === expected[i]);
    }

    case 'fill_blank': {
      if (d.format !== 'fill_blank') return false;
      return q.options.every(
        (opt, i) => stripSuffix(d.blanks[i] ?? '') === opt.correct
      );
    }

    case 'grouping': {
      if (d.format !== 'grouping') return false;
      // For every item the chosen category must match the group it actually
      // belongs to in the question definition.
      return d.items.every((item, i) => {
        const owning = q.groups.find((g) => g.items.includes(item))?.category;
        return owning != null && owning === d.assignments[i];
      });
    }

    case 'matching': {
      if (d.format !== 'matching') return false;
      // Every left slot's right pick must point to the same pair index.
      return d.matches.every((rightSlot, leftSlot) => {
        if (rightSlot === null) return false;
        const leftPairIdx = d.leftOrder[leftSlot];
        const rightPairIdx = d.rightOrder[rightSlot];
        return leftPairIdx === rightPairIdx;
      });
    }

    case 'open_ended':
      throw new Error('open_ended uses async AI evaluation, not isCorrect()');
  }
}
